import {
  COOKIE_SERVICE,
  type CookieService,
  type Logger,
  LOGGER,
} from "@packages/common";
import { CONFIG, type Config } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import {
  COOKIE_CONSENTS_SERVICE,
  type CookieConsentsService,
} from "../cookie-consents";
import type { LastSearchCookieDTO } from "./dtos/";
import { type CookieKey, LAST_SEARCH_SERVICE } from "./last-search.types";

@Injectable(LAST_SEARCH_SERVICE)
export class LastSearchService {
  private readonly COOKIE_NAME: string;
  private readonly logger: Logger;
  constructor(
    @Inject(CONFIG) private readonly config: Config,
    @Inject(LOGGER) private readonly loggerFactory: Logger,
    @Inject(COOKIE_SERVICE) private readonly cookieService: CookieService,
    @Inject(COOKIE_CONSENTS_SERVICE)
    private readonly cookieConsentsService: CookieConsentsService
  ) {
    this.logger = this.loggerFactory.create(LAST_SEARCH_SERVICE);
    this.COOKIE_NAME = this.config.lastSearchCookieName || "";
  }

  clearAll() {
    this.logger.log("[clearAll] clearing search cookies: ", this.COOKIE_NAME);

    this.cookieService.removeCookie(this.COOKIE_NAME);
  }

  /**
   * Save the most recent search URL in the cookie for the specified vehicle type.
   *
   * @param url - The URL to be saved in the cookie.
   */
  async save(url: string): Promise<void> {
    try {
      if (!this.COOKIE_NAME) {
        return;
      }

      if (!url) {
        return;
      }

      if (!(await this.cookieConsentsService.isSearchEnabled())) {
        return;
      }

      const vehicleType = this.getVehicleType(url);

      if (!vehicleType) {
        return;
      }

      const lastSearchCookie = this.get();

      if (!lastSearchCookie) {
        const newCookie: LastSearchCookieDTO = { [vehicleType]: url };
        this.cookieService.save(this.COOKIE_NAME, newCookie);
        return;
      }

      if (lastSearchCookie[vehicleType] === url) {
        return;
      }

      if (!lastSearchCookie || lastSearchCookie[vehicleType] === url) {
        return;
      }

      const updatedCookie = {
        ...lastSearchCookie,
        [vehicleType]: url,
      };

      this.cookieService.save(this.COOKIE_NAME, updatedCookie);
    } catch (error) {
      this.logger.error("[Set] Failed to set lastSearchCookie.", error);
    }
  }

  /**
   * Return the most recent search URL from the cookie for the specified key.
   *
   * @param key - The key to retrieve the URL for.
   * @returns The most recent search URL from the cookie, or null if not found.
   */
  get(): LastSearchCookieDTO | null {
    try {
      const lastSearchCookie = this.cookieService.get<LastSearchCookieDTO>(
        this.COOKIE_NAME
      );

      if (!lastSearchCookie) {
        return null;
      }

      return lastSearchCookie;
    } catch (error) {
      this.logger.error("[Get] Failed to get lastSearchCookie.", error);
      return null;
    }
  }

  /**
   * Determine the vehicle type based on the given URL.
   *
   * @param url - The URL to determine the vehicle type from.
   * @returns The vehicle type as a {@link CookieKey}, or null if not found.
   */
  private getVehicleType(url: string): CookieKey | null {
    if (!url) {
      return null;
    }
    if (url.startsWith("/motos") || url.search("tipoveiculo=motos") !== -1) {
      return "bike";
    }
    if (url.startsWith("/carros") || url.search("tipoveiculo=carros") !== -1) {
      return "car";
    }

    return null;
  }
}
